/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { appRoute } from '@utils/globalConstant';
import { Alert } from '@core/Utils';
import { Loader } from '@utils/Icons/Loader';
import { Meta } from '@core/Meta';
import { useA11y } from '@context/Utils/A11y';
import { useLayout } from '@context/Utils';
import { useTitle } from '@context/Utils/Title';

export function withLayout(WrappedComponent) {
  // Wrap all of the components into one layout.

  function Layout(props) {
    // State
    const location = useLocation();
    const { announceTitle } = useTitle();
    const { tA11yTranslate, getGlobalMetaTitles } = useA11y();
    const { isLoading } = useLayout();
    const hasAnnouncedRef = useRef(false);
    const currentPathRef = useRef(location.pathname);

    // META: Page titles
    // NOTE: To override the page titles defined in this file, import the `Meta` component directly into the desired page and set the title there.

    // global page titles
    const { globalPageTitle, globalSuccessPageTitle, globalErrorPageTitle } = getGlobalMetaTitles();

    // main pages page titles
    const activationPageTitle = tA11yTranslate('ek_global_meta_title_activation').content;
    const errorPageTitle = tA11yTranslate('ek_global_meta_title_404_txt').content;
    const routeToTitleMap = {
      // for exact route matches
      [appRoute.HOME_PAGE]: `${activationPageTitle}`,
      [appRoute.NOT_FOUND]: `${errorPageTitle}`
    };

    function getPageTitleForPath(currentRoute) {
      // check for exact route matches
      if (routeToTitleMap[currentRoute]) {
        return `${globalPageTitle} - ${routeToTitleMap[currentRoute]}`;
      }
      // check for nested routes
      if (routeToTitleMap.prefix) {
        for (const { prefix, title } of routeToTitleMap.prefix) {
          if (currentRoute.startsWith(prefix)) {
            return `${globalPageTitle} - ${title}`;
          }
        }
      }
      // fallback
      return globalPageTitle;
    }

    const pageTitle = getPageTitleForPath(location.pathname);

    // Wait until translation is ready (i.e., not the key itself)
    const isTranslationReady = pageTitle && 
      !pageTitle.includes('ek_global_meta_title') &&
      globalPageTitle && 
      !globalPageTitle.includes('ek_global_meta_title');

    if (currentPathRef.current !== location.pathname) {
      hasAnnouncedRef.current = false;
      currentPathRef.current = location.pathname;
    }

    useEffect(() => {
      if (isTranslationReady && !hasAnnouncedRef.current) {
        const timeoutId = setTimeout(() => {
          announceTitle(pageTitle, true); 
          hasAnnouncedRef.current = true;
        }, 500); 

        return () => clearTimeout(timeoutId);
      }
    }, [isTranslationReady, pageTitle, announceTitle]);

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (isTranslationReady && !hasAnnouncedRef.current) {
          announceTitle(pageTitle, true);
          hasAnnouncedRef.current = true;
        }
      }, 800); 

      return () => clearTimeout(timeoutId);
    }, []); 

    // BFSG: check unused routes
    if (
      location.pathname === '/activation' ||
      // location.pathname === '/activation/payment-verification' ||
      location.pathname === '/login' ||
      location.pathname === '/force-password-change' ||
      location.pathname === '/forgot-password' ||
      location.pathname === '/' ||
      location.pathname === '/store-review' || 
      location.pathname === '/test'
    ) {
      return (
        <div>
          {isLoading && (
            <>
              {isTranslationReady && <Meta title={pageTitle} key={`meta-step-${pageTitle}`} />}
              <Loader />
            </>
          )}
          {!isLoading && (
            <>
              {isTranslationReady && <Meta title={pageTitle} key={`meta-step-${pageTitle}`} />}
              <Alert />
              <WrappedComponent {...props} />
            </>
          )}
        </div>
      );
    }

    return (
      <>
        {isTranslationReady && <Meta title={pageTitle} key={`meta-step-${pageTitle}`} />}
        <WrappedComponent {...props} />
      </>
    );
  }

  return Layout;
}

export default withLayout;